import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiService from "../../api/axiosInstance";
import ParentAuthLayout from "./ParentAuthLayout";
import { useDispatch } from "react-redux";
import {
  PARENT_BOOKING_HUB_PATH,
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";
import {
  startJourney,
  logParentLoginApiResult,
  logParentLoginPageViewed,
  logParentLoginSubmitted,
} from "../../utils/datadogLogger";
import { finalizeParentLogin } from "../../features/parentFlow/parentAuth";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const GENERIC_PARENT_LOGIN_ERROR = "User Not Found";

const ParentLoginPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const redirectPath =
    location.state?.redirectTo ||
    readParentPostAuthRedirect() ||
    PARENT_BOOKING_HUB_PATH;

  useEffect(() => {
    persistParentPostAuthRedirect(redirectPath);
  }, [redirectPath]);

  useEffect(() => {
    logParentLoginPageViewed(redirectPath || null);
  }, [redirectPath]);

  const loginMutation = useMutation({
    mutationFn: (payload) => apiService.post("api/login", payload),
    onSuccess: (response) => {
      const profileData = response?.data?.data || {};
      logParentLoginApiResult(
        profileData?.email || formData.email.trim(),
        response?.status || 200,
        response?.data || {}
      );
      finalizeParentLogin({
        dispatch,
        navigate,
        profileData,
        redirectPath,
      });
    },
    onError: (error) => {
      logParentLoginApiResult(
        formData.email.trim(),
        error?.response?.status || 500,
        error?.response?.data || {},
        error?.response?.data?.message || error?.message || "Parent login failed."
      );
      setErrors({
        submit: GENERIC_PARENT_LOGIN_ERROR,
      });
    },
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value.trimStart() }));
    setErrors((current) => ({ ...current, [name]: "", submit: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!formData.email) {
      nextErrors.email = "Email is required.";
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    startJourney("parent_login");
    logParentLoginSubmitted(formData.email.trim(), redirectPath || null);
    loginMutation.mutate({
      email: formData.email.trim(),
      password: formData.password,
    });
  };

  const inputClassName =
    "w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3 text-base text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white";

  return (
    <ParentAuthLayout
      title="Parent Login"
      description="Sign in to stay connected with your child’s learning and manage everything in one place."
      accent="Simple, convenient access for parents"
      accentDescription="Book sessions, keep track of learning time, and make the most of your subscription with tools that support steady progress."
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#08059B]">
            Parent portal login
          </p>
          <h2 className="mt-2 text-3xl font-bold text-[#111827]">
            Login
          </h2>
        </div>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="parent-login-email"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Email
          </label>
          <input
            id="parent-login-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className={inputClassName}
            placeholder="enter email"
            autoComplete="email"
          />
          {errors.email ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-login-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="parent-login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="enter password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.password}
            </p>
          ) : null}
        </div>

        {errors.submit ? (
          <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {errors.submit}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Link
            className="text-sm font-bold text-[#08059B] underline"
            to="/parent/forgot-password"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="inline-flex w-full items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loginMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              Logging in...
            </span>
          ) : (
            "Login"
          )}
        </button>
      </form>

      <div className="mt-8 text-center text-sm leading-7 text-[#475569]">
        <Link
          className="text-lg font-bold text-[#08059B] underline sm:text-xl"
          to="/parent/signup"
        >
          Sign Up as a Parent.
        </Link>
      </div>
    </ParentAuthLayout>
  );
};

export default ParentLoginPage;
